import React, { createContext, useContext, useEffect, useState } from 'react';
import { validateAuth, isAuthenticated } from '../services/auth';
import useUserStore from '../store/useUserStore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const { isAuthenticated: userIsAuthenticated, clearUser } = useUserStore();

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsLoading(true);
        
        // Check if user is authenticated in store
        if (!userIsAuthenticated) {
          // Validate token if it exists
          const authResult = await validateAuth();
          
          if (!authResult.isValid) {
            // Clear user data if token is invalid
            clearUser();
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
        }
        
        setAuthChecked(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        clearUser();
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setAuthChecked(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [userIsAuthenticated, clearUser]);

  // Set up periodic token validation
  useEffect(() => {
    if (!authChecked) return;

    const interval = setInterval(async () => {
      try {
        const authResult = await validateAuth();
        if (!authResult.isValid) {
          console.log('Token validation failed:', authResult.reason);
          clearUser();
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (error) {
        console.error('Periodic auth check failed:', error);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [authChecked, clearUser]);

  const value = {
    isLoading,
    authChecked,
    isAuthenticated: isAuthenticated(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 